/**
 * @reflecto/client-core — sse/sse-client.ts
 *
 * Transport for the server's SSE stream. Framework-agnostic — uses only
 * `fetch`, `AbortController`, `ReadableStream`, and `TextDecoder`, all
 * of which are available in modern browsers and Service Workers. The
 * class itself does NOT schedule reconnects — after a disconnect it
 * simply returns; the host decides when to call `connect()` again.
 * The extension drives that with a `chrome.alarms` keepalive; other
 * hosts (web, Electron/Tauri) will each wire up their own reconnect
 * tick before relying on this transport for long-lived streams.
 */

export interface SSEEvent {
  event: string;
  data: string;
  id?: string;
}

export interface SSEClientOptions {
  /** Base API URL (e.g. `https://sync.reflecto.dev/v1`). The transport appends `/stream` to form the connection endpoint — callers must NOT include the path themselves. */
  url: string;
  /** JWT device token. Sent as `Authorization: Bearer <token>`. */
  token: string;
  /** Called for every parsed server event. Awaited so the loop serialises event processing. */
  onEvent: (event: SSEEvent) => void | Promise<void>;
  /** Called whenever the underlying connection transitions open/closed. */
  onStatusChange?: (connected: boolean) => void;
  /** High-water seqId cursor; sent as `Last-Event-ID` so the server skips already-seen queued messages. */
  lastEventId?: number;
  /** Called when the server rejects the connection because the pairing is invalid (404/401/403). */
  onPairingLost?: () => void;
  /**
   * Called for non-abort connection failures (network drop, non-2xx
   * status, parser exception). Diagnostic-only — the host still
   * receives `onStatusChange(false)` and is responsible for any
   * retry. AbortError is intentionally NOT surfaced here since it
   * represents an expected teardown initiated by the host itself.
   */
  onError?: (err: unknown) => void;
}

export class SSEClient {
  private controller: AbortController | null = null;
  private _connected = false;
  private options: SSEClientOptions | null = null;

  get isConnected(): boolean {
    return this._connected;
  }

  connect(options: SSEClientOptions): void {
    this.options = options;
    this.startConnection();
  }

  disconnect(): void {
    // cleanup() calls setConnected(false) which fires onStatusChange — must happen
    // before options is nulled, otherwise the callback is silently dropped.
    this.cleanup();
    this.options = null;
  }

  private cleanup(): void {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
    this.setConnected(false);
  }

  private setConnected(value: boolean): void {
    if (this._connected !== value) {
      this._connected = value;
      this.options?.onStatusChange?.(value);
    }
  }

  private async startConnection(): Promise<void> {
    if (!this.options) return;

    this.cleanup();
    const { url, token, onEvent, lastEventId } = this.options;
    this.controller = new AbortController();

    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (typeof lastEventId === "number" && lastEventId > 0) {
      headers["Last-Event-ID"] = String(lastEventId);
    }

    try {
      const res = await fetch(`${url}/stream`, {
        headers,
        signal: this.controller.signal,
      });

      if (!res.ok || !res.body) {
        if (res.status === 404 || res.status === 401 || res.status === 403) {
          // Pairing is invalid — don't reconnect, notify caller
          this.options?.onPairingLost?.();
          this.options = null; // prevent reconnect
          return;
        }
        throw new Error(`SSE connect failed (${res.status})`);
      }

      this.setConnected(true);

      let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
      try {
        reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const events = parseSSEBuffer(buffer);
          buffer = events.remaining;

          for (const event of events.parsed) {
            await onEvent(event);
          }
        }
      } finally {
        // Release the body lock on every exit path (normal loop exit,
        // thrown error, or AbortError). Without this the reader stays
        // locked and the HTTP connection can be held open until the
        // host tears the page / worker down — including across
        // extension service-worker suspension/revival cycles and
        // web-app tab hibernation.
        reader?.cancel().catch(() => {});
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") {
        // Intentional disconnect — `disconnect()` already called
        // `cleanup()` which fired `setConnected(false)`. Returning
        // here avoids a redundant second `setConnected(false)` call
        // and the duplicate `onStatusChange(false)` it would emit.
        return;
      }
      // Non-abort error: connection failed unexpectedly. Surface the
      // reason via onError for diagnostics, then fall through to
      // notify the host of the disconnected state below.
      this.options?.onError?.(err);
    }

    // Reached on natural stream end (server closed) or a swallowed
    // non-abort error. SSEClient does not schedule reconnects — the
    // host (e.g. the extension's chrome.alarms keepalive) is
    // responsible for calling connect() again if it wants to retry.
    this.setConnected(false);
  }
}

/**
 * Parse a raw SSE buffer into discrete events. Handles `\n\n` boundaries,
 * comments, multi-line data, and CRLF normalisation.
 *
 * CRLF handling: `\r\n` pairs are collapsed to `\n` before splitting.
 * A lone trailing `\r` at the end of the buffer is kept as-is so a
 * CRLF split across two reader chunks (buffer ends with `\r`, next
 * chunk starts with `\n`) reassembles cleanly into `\r\n` → `\n` on
 * the subsequent call. The per-line loop strips any residual trailing
 * `\r` defensively.
 */
export function parseSSEBuffer(buffer: string): { parsed: SSEEvent[]; remaining: string } {
  const normalised = buffer.replace(/\r\n/g, "\n");
  const parsed: SSEEvent[] = [];
  const blocks = normalised.split("\n\n");

  // Last element is incomplete if buffer didn't end with \n\n
  const remaining = normalised.endsWith("\n\n") ? "" : (blocks.pop() ?? "");

  for (const block of blocks) {
    if (!block.trim()) continue;

    let event = "message";
    let data = "";
    let id: string | undefined;
    let sawField = false;

    for (const rawLine of block.split("\n")) {
      const line = rawLine.replace(/\r$/, "");
      if (line.startsWith(":")) continue; // SSE comment (e.g. ": ping", ":keepalive", ":")
      if (line === "") continue; // blank line inside a block has no effect

      // Per the SSE spec: a line with no colon is treated as a field
      // name with an empty value (e.g. a bare `data` line appends the
      // empty string to the data buffer).
      const colonIdx = line.indexOf(":");
      const field = colonIdx === -1 ? line : line.slice(0, colonIdx);
      const value = colonIdx === -1 ? "" : line.slice(colonIdx + 1).trimStart();

      switch (field) {
        case "event":
          event = value;
          sawField = true;
          break;
        case "data":
          data += (data ? "\n" : "") + value;
          sawField = true;
          break;
        case "id":
          id = value;
          sawField = true;
          break;
      }
    }

    // The SSE spec dispatches any block that supplied at least one
    // recognised field — including an explicit `event:` with no
    // `data:` body. Dropping those would silently swallow server
    // signals (e.g. a bare "confirmed" or "expired" event).
    if (sawField) {
      parsed.push({ event, data, id });
    }
  }

  return { parsed, remaining };
}
